import { useAppConfig } from '@/hooks/useAppConfig';
import { useLocalize } from '@/utils/localize';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (templateId: string, prompt: string, styleId: string, ratio: string) => void;
  category?: string;
  categoryTitleKey?: string;
}

export default function TemplateModal({ isOpen, onClose, onSelect, category, categoryTitleKey }: TemplateModalProps) {
  const config = useAppConfig();
  const allTemplates = config?.templates ?? [];
  const templates = category ? allTemplates.filter((tpl) => tpl.category === category) : allTemplates;
  const { t } = useTranslation();
  const localize = useLocalize();

  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
  };

  return (
    <div data-testid="template-modal" className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose}></div>

      <div className="relative w-full bg-white rounded-t-3xl max-h-[80vh] overflow-hidden animate-slide-up">
        {/* 头部 */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold text-gray-800">{categoryTitleKey ? t(categoryTitleKey) : t('gen_selectTemplate')}</h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        {/* 模板列表 */}
        <div className="px-5 overflow-y-auto max-h-[calc(80vh-60px)]" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
          <div className="grid grid-cols-2 gap-3">
            {templates.map((template) => (
              <button
                key={template.id}
                data-testid={`template-item-${template.id}`}
                data-prompt={template.default_prompt ?? ''}
                onClick={() => {
                  onSelect(
                    template.id,
                    template.default_prompt ?? '',
                    template.recommend_style_id ?? '',
                    template.recommend_ratio ?? '',
                  );
                  handleClose();
                }}
                className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-95 transition-transform text-left"
              >
                <div className="aspect-[3/4] w-full">
                  <img
                    src={template.thumbnail_url}
                    alt={localize(template, 'name')}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white font-semibold text-sm">{localize(template, 'name')}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
